import { useNavigate } from "react-router-dom";
import { useTheme } from "./ThemeContext";

export default function AssignmentEvaluatorDetail() {
  const navigate = useNavigate();
  const { isDarkMode, toggleTheme } = useTheme();

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600&display=swap');
    
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    
    :root {
      --ink: #0f1117;
      --ink-soft: #4a4f5e;
      --ink-faint: #8b91a1;
      --bg: #f7f8fa;
      --surface: #ffffff;
      --border: #e4e6ed;
      --radius: 14px;
      --shadow: 0 2px 12px rgba(15,17,23,.07);
      --grad: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
    }
    
    [data-theme="dark"] {
      --ink: #f7f8fa;
      --ink-soft: #cbd5e1;
      --bg: #0f1117;
      --surface: #1a1f3a;
      --border: #2d3748;
    }
    
    body { font-family: 'DM Sans', sans-serif; background: var(--bg); color: var(--ink); line-height: 1.7; }
    
    nav {
      position: sticky; top: 0; z-index: 100;
      background: rgba(247,248,250,.88);
      backdrop-filter: blur(14px);
      border-bottom: 1px solid var(--border);
      padding: 0 2rem;
      display: flex; align-items: center; justify-content: space-between;
      height: 64px;
    }
    [data-theme="dark"] nav { background: rgba(15,17,23,.88); }
    
    .nav-back { cursor: pointer; font-size: 1.2rem; color: var(--ink); transition: opacity .2s; }
    .nav-back:hover { opacity: .6; }
    
    .nav-theme-toggle {
      background: transparent; border: none; cursor: pointer;
      font-size: 1.2rem; color: var(--ink);
      padding: .4rem; border-radius: 6px;
      transition: background .2s;
    }
    .nav-theme-toggle:hover { background: rgba(102,126,234,.1); }
    
    .hero {
      padding: 4rem 2rem;
      background: var(--grad);
      color: #fff;
      text-align: center;
    }
    
    h1 {
      font-family: 'DM Serif Display', serif;
      font-size: 2.8rem;
      margin-bottom: 1rem;
    }
    
    .hero p {
      font-size: 1.2rem;
      max-width: 600px;
      margin: 0 auto;
      opacity: .95;
    }
    
    .content {
      max-width: 1000px;
      margin: 0 auto;
      padding: 3rem 2rem;
    }
    
    h2 {
      font-family: 'DM Serif Display', serif;
      font-size: 1.8rem;
      margin: 2.5rem 0 1.5rem;
      color: var(--ink);
    }
    
    h3 {
      font-size: 1.3rem;
      margin: 1.5rem 0 .8rem;
      color: var(--ink);
    }
    
    p {
      color: var(--ink-soft);
      margin-bottom: 1rem;
      line-height: 1.8;
    }
    
    .features-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 1.5rem;
      margin: 2rem 0;
    }
    
    .feature-card {
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: var(--radius);
      padding: 1.8rem;
      transition: all .2s;
    }
    
    .feature-card:hover {
      box-shadow: 0 8px 24px rgba(102,126,234,.15);
      transform: translateY(-4px);
    }
    
    .feature-icon {
      font-size: 2rem;
      margin-bottom: .8rem;
    }
    
    .feature-card h3 {
      margin-top: 0;
      font-size: 1.1rem;
    }
    
    .feature-card p {
      font-size: .95rem;
      margin: 0;
    }
    
    .benefits {
      background: linear-gradient(135deg, rgba(102,126,234,.08) 0%, rgba(118,75,162,.05) 100%);
      border-left: 4px solid #667eea;
      border-radius: var(--radius);
      padding: 2rem;
      margin: 2rem 0;
    }
    
    .benefits ul {
      list-style: none;
      margin: 1rem 0 0;
    }
    
    .benefits li {
      color: var(--ink-soft);
      margin-bottom: .8rem;
      padding-left: 1.5rem;
      position: relative;
    }
    
    .benefits li::before {
      content: '✓';
      position: absolute;
      left: 0;
      color: #667eea;
      font-weight: bold;
      font-size: 1.1rem;
    }
    
    .how-to {
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: var(--radius);
      padding: 2rem;
      margin: 2rem 0;
    }
    
    .step {
      margin-bottom: 2rem;
    }
    
    .step-number {
      display: inline-block;
      width: 40px;
      height: 40px;
      background: var(--grad);
      color: #fff;
      border-radius: 50%;
      text-align: center;
      line-height: 40px;
      font-weight: 600;
      margin-right: 1rem;
    }
    
    .step h4 {
      display: inline-block;
      font-size: 1.1rem;
      color: var(--ink);
    }
    
    .step p {
      margin-left: 4rem;
      margin-top: .5rem;
    }
    
    .cta-button {
      background: var(--grad);
      color: #fff;
      padding: 1.2rem 2.5rem;
      border: none;
      border-radius: 10px;
      font-size: 1.05rem;
      font-weight: 600;
      cursor: pointer;
      transition: all .2s;
      display: inline-block;
      text-decoration: none;
      margin-top: 2rem;
    }
    
    .cta-button:hover {
      transform: translateY(-3px);
      box-shadow: 0 12px 32px rgba(102,126,234,.4);
    }
    
    footer {
      border-top: 1px solid var(--border);
      padding: 2rem;
      text-align: center;
      color: var(--ink-soft);
      margin-top: 4rem;
    }
  `;

  return (
    <>
      <style>{styles}</style>

      <nav>
        <div className="nav-back" onClick={() => navigate('/')}>← Back</div>
        <button className="nav-theme-toggle" onClick={toggleTheme}>
          {isDarkMode ? '☀️' : '🌙'}
        </button>
      </nav>

      <div className="hero">
        <h1>🎓 Assignment Evaluator</h1>
        <p>AI-powered feedback on your academic work using advanced Claude AI. Get instant, detailed evaluation with rubric breakdowns and improvement suggestions.</p>
      </div>

      <div className="content">
        <section>
          <h2>What is Assignment Evaluator?</h2>
          <p>
            Assignment Evaluator is an intelligent tool designed to provide comprehensive feedback on your academic assignments. 
            Powered by Claude AI, it analyzes your work against custom rubrics and academic standards, delivering actionable insights 
            to help you improve your writing and understanding.
          </p>
          <p>
            Whether you're working on essays, research papers, lab reports, or other assignments, our tool provides constructive 
            feedback that mirrors what real academic reviewers look for.
          </p>
        </section>

        <section>
          <h2>Key Features</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">📄</div>
              <h3>Multi-Format Support</h3>
              <p>Upload PDF, DOCX, TXT, or Markdown files. Our AI handles various file formats seamlessly.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📊</div>
              <h3>Rubric Analysis</h3>
              <p>Define custom rubrics and watch as the AI evaluates your work against your criteria.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">⚡</div>
              <h3>Instant Feedback</h3>
              <p>Get detailed evaluation results in seconds, not hours. Iterate quickly.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🎯</div>
              <h3>Improvement Tips</h3>
              <p>Receive specific, actionable suggestions for strengthening your work.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🔍</div>
              <h3>Strength Analysis</h3>
              <p>Understand what you're doing well so you can build on those strengths.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🚀</div>
              <h3>Score Calculation</h3>
              <p>Get a clear score based on rubric criteria and academic standards.</p>
            </div>
          </div>
        </section>

        <section>
          <h2>Benefits</h2>
          <div className="benefits">
            <p><strong>For Students:</strong></p>
            <ul>
              <li>Improve writing quality before submission</li>
              <li>Understand assignment requirements better</li>
              <li>Practice self-directed learning and feedback integration</li>
              <li>Save time with instant evaluation</li>
              <li>Build stronger academic skills</li>
              <li>Gain confidence in your work</li>
            </ul>
          </div>
        </section>

        <section>
          <h2>How to Use</h2>
          <div className="how-to">
            <div className="step">
              <span className="step-number">1</span>
              <h4>Upload Your Assignment</h4>
              <p>Click the upload area or drag-and-drop your assignment file (PDF, DOCX, TXT, or Markdown)</p>
            </div>
            <div className="step">
              <span className="step-number">2</span>
              <h4>Set Your Rubric (Optional)</h4>
              <p>Enter evaluation criteria if you have specific rubric requirements. This helps the AI tailor feedback to your needs.</p>
            </div>
            <div className="step">
              <span className="step-number">3</span>
              <h4>Add Context (Optional)</h4>
              <p>Specify the subject, assignment type, or academic level to get more targeted feedback.</p>
            </div>
            <div className="step">
              <span className="step-number">4</span>
              <h4>Provide Your API Key</h4>
              <p>Enter your Claude API key (kept secure and never stored). Get one free at console.anthropic.com</p>
            </div>
            <div className="step">
              <span className="step-number">5</span>
              <h4>Get Evaluation</h4>
              <p>Receive detailed feedback with scores, strengths, weaknesses, and improvement suggestions</p>
            </div>
          </div>
        </section>

        <section>
          <h2>Use Cases</h2>
          <p>
            <strong>Essays & Literary Analysis:</strong> Get feedback on thesis strength, argumentation, and structure.
          </p>
          <p>
            <strong>Research Papers:</strong> Evaluate research methodology, citation quality, and academic rigor.
          </p>
          <p>
            <strong>Lab Reports:</strong> Review experimental design communication and results presentation.
          </p>
          <p>
            <strong>Case Studies:</strong> Assess analysis depth, practical application, and critical thinking.
          </p>
          <p>
            <strong>Problem Sets:</strong> Verify solutions and reasoning clarity for quantitative work.
          </p>
        </section>

        <button className="cta-button" onClick={() => navigate('/tools/assignment-evaluator')}>
          Start Evaluating Now
        </button>
      </div>

      <footer>
        <p>© 2026 flycodeinv.com · All Rights Reserved</p>
      </footer>
    </>
  );
}
